import { useMatches } from '@tanstack/react-router'
import type { TasksTab } from '@/lib/tasks-search'

/**
 * Which tab of the /tasks board is showing, from the route that is showing it.
 *
 * Off the *matched route* (`staticData.tasksTab`, see `tasksView` in
 * router.tsx), not the pathname. `state.matches` changes only when a navigation
 * commits, so this is right on the way in and stable on the way out: while the
 * board is still mounted over a pending navigation to `/tasks/<uuid>`, it keeps
 * rendering the tab it is on rather than reacting to an address that is no
 * longer the board's.
 *
 * **Undefined means undefined.** Every route that paints the board declares its
 * tab, so this cannot come back empty — and if a new one ever forgets to, the
 * caller renders no tab rather than the first one. Defaulting to `'list'` here
 * would put back the thing this replaced: coming back to the Calendar or
 * Collections tab, a frame of some other tab before the right one. Reading the
 * pathname had the same shape — it had to answer "which tab?" for
 * `/tasks/<uuid>` mid-transition, and every answer to that is wrong.
 */
export function useTasksTab(): TasksTab | undefined {
  return useMatches({
    select: (matches) => {
      for (let i = matches.length - 1; i >= 0; i--) {
        const tasksTab = matches[i].staticData.tasksTab
        if (tasksTab) return tasksTab
      }
      return undefined
    },
  })
}
